import React, { FC, useState } from 'react';
import classnames from 'classnames';

import styles from './Avatar.module.css';

export type AvatarSize = 48 | 124 | 132 | 160;
type AvatarLoading = 'lazy' | 'eager';

export interface AvatarProps {
    avatarUrl?: string;
    avatarSize: AvatarSize;
    hasSubscriptions?: boolean;
    withSubProfileAnimation?: boolean;
    isMoreContrastDefaultAvatar?: boolean;
    isActiveUser?: boolean;
    className?: string;
    alt?: string;
    loading?: AvatarLoading;
}

export const Avatar: FC<AvatarProps> = ({
    avatarUrl,
    avatarSize,
    hasSubscriptions = false,
    withSubProfileAnimation = false,
    isMoreContrastDefaultAvatar = false,
    isActiveUser = true,
    className,
    loading = 'lazy',
    alt = '',
}) => {
    const [isAvatarLoaded, setIsAvatarLoaded] = useState(false);
    const [isAvatarHidden, setIsAvatarHidden] = useState(false);

    const sizeClass = styles[`size${avatarSize}`];
    const shouldShowImage = avatarUrl && isActiveUser && !isAvatarHidden;
    const isDefaultAvatar = !avatarUrl || !isAvatarLoaded || isAvatarHidden;

    return (
        <div
            className={classnames(className, styles.root, sizeClass, {
                [styles.hasSubscriptions]: hasSubscriptions,
                [styles.withSubProfileAnimation]: withSubProfileAnimation,
            })}
        >
            {withSubProfileAnimation && (
                <>
                    <div
                        className={classnames(
                            styles.imageAnimationLayer,
                            styles.imageAnimationLayer_1
                        )}
                    />
                    <div
                        className={classnames(
                            styles.imageAnimationLayer,
                            styles.imageAnimationLayer_2
                        )}
                    />
                </>
            )}
            <div
                className={classnames(styles.avatarWrapper, sizeClass, {
                    [styles.isMoreContrastDefaultAvatar]: isMoreContrastDefaultAvatar,
                    [styles.isDeleted]: !isActiveUser,
                    [styles.defaultAvatar]: isDefaultAvatar,
                    [styles.backgroundImageHidden]: isAvatarLoaded,
                })}
            >
                {shouldShowImage ? (
                    <img
                        className={styles.avatar}
                        src={avatarUrl}
                        onLoad={() => setIsAvatarLoaded(true)}
                        onError={() => setIsAvatarHidden(true)}
                        alt={alt}
                        loading={loading}
                    />
                ) : null}
            </div>
        </div>
    );
};
